import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowUp, ChevronUp, Clock, RotateCcw, Search, Sparkles } from 'lucide-react';

import { ProfileCardV2 as ProfileCard } from './ProfileCardV2';
import { haptic } from '../utils/haptics';
import Button from './ui/Button';
import Skeleton from './ui/Skeleton.jsx';
import OtterCompactLogo from './ui/OtterCompactLogo';
import { getCachedSchoolLogo } from '../utils/schoolLogo';

/**
 * MatchRoundView — "Deine 6 für heute".
 *
 * Tritt an die Stelle der Endlosliste aus HomeView.js (die unangetastet als
 * Rückfallebene liegen bleibt). Der Unterschied ist nicht die Optik, sondern die
 * Mechanik: ein Set mit Anfang und Ende statt endlosem Nachladen. Deshalb gibt es
 * hier weder "Weitere Profile laden" noch einen Fortschrittszähler — dafür eine
 * Schlusskarte, die den Tag ehrlich abschließt.
 *
 * Der Kartenbereich (.match-feed) ist auf dem Telefon sein EIGENER Scroll-Container.
 * Das ist Voraussetzung und nicht Geschmack: `scroll-snap-type` wirkt nur am
 * Element, das tatsächlich scrollt. Bis August 2026 stand es auf dem Raster-Div,
 * gescrollt hat aber das Dokument — das Einrasten war wirkungslos, und weil die
 * Karte zusätzlich bei 620px deckelte, stand auf großen Telefonen immer ein Stück
 * der Nachbarkarte im Bild. Jetzt gilt: eine Karte = eine Bildschirmseite.
 */

/** Formatiert Restsekunden als "3 Std. 20 Min." bzw. "20 Min.". */
const formatCountdown = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours > 0) return `${hours} Std. ${minutes} Min.`;
  return `${Math.max(1, minutes)} Min.`;
};

/**
 * Positionsanzeige am rechten Rand: ein Strich je Karte, der aktuelle gefüllt.
 *
 * Kodiert echte Information (wo in der Runde stehe ich), statt zu schmücken —
 * deshalb erscheint sie nur, wenn es überhaupt mehrere Karten gibt, und nur auf
 * dem Telefon, wo immer nur eine Karte sichtbar ist.
 *
 * `absolute` statt `fixed`: Die Leiste gehört zum Kartenbereich, nicht zum
 * Fenster — mit `fixed` hing sie auch dann noch am Bildschirmrand, wenn der
 * Feed gar nicht die volle Höhe hatte.
 */
const RoundProgress = memo(({ total, activeIndex }) => {
  if (total < 2) return null;
  return (
    <div
      className="absolute right-0.5 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-1.5 md:hidden"
      aria-hidden="true"
    >
      {Array.from({ length: total }).map((_, index) => (
        <span
          key={index}
          className={`w-0.5 rounded-full transition-all duration-300 ${
            index === activeIndex ? 'h-5 bg-primary' : 'h-2 bg-foreground/15'
          }`}
        />
      ))}
    </div>
  );
});
RoundProgress.displayName = 'RoundProgress';
RoundProgress.propTypes = {
  total: PropTypes.number.isRequired,
  activeIndex: PropTypes.number.isRequired
};

/**
 * Die kurze Bestätigung nach dem Mischen.
 *
 * Ohne sie war das Mischen unsichtbar: Der Knopf sitzt unten auf der
 * Schlusskarte, die neuen Karten liegen oben — wer nicht zufällig nach oben
 * scrollte, sah nach dem Tippen exakt dasselbe Bild wie vorher.
 */
const FreshRoundPill = memo(({ label }) => {
  if (!label) return null;
  return (
    /* Gerade und zurückhaltend (bis Okt 2026 ein schiefer, poppender Störer).
       Die ganze Bewegung steckt in CSS (`.fresh-round-pill`, index.css): aus der
       Unschärfe herabgleiten, stehen, nach oben verwehen, abgestimmt auf die
       2,2 s, nach denen MatchRoundView den Hinweis wieder entfernt. Der Punkt
       sendet einmal einen Ring aus, die Haarlinie unten läuft ab. */
    <div
      role="status"
      className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 z-30"
    >
      <span className="fresh-round-pill relative inline-flex items-center gap-2 overflow-hidden whitespace-nowrap rounded-full border border-border/70 bg-surface px-3.5 py-1.5 text-xs font-medium text-foreground shadow-token-md">
        <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
          <span className="fresh-round-ping absolute inset-0 rounded-full bg-primary" />
          <span className="relative h-1.5 w-1.5 rounded-full bg-primary" />
        </span>
        {label}
        <span
          className="fresh-round-drain absolute inset-x-3 bottom-0 h-px bg-primary/50"
          aria-hidden="true"
        />
      </span>
    </div>
  );
});
FreshRoundPill.displayName = 'FreshRoundPill';
FreshRoundPill.propTypes = { label: PropTypes.string };

/** Was die Runde ausmacht — drei Zeilen, keine Werbetexte. */
const START_HINTS = [
  { Icon: Sparkles, text: 'Sechs frische Vorschläge' },
  { Icon: RotateCcw, text: 'Dreimal am Tag neu mischen' }
];

/**
 * Die Einstiegskarte vor der ersten Runde.
 *
 * Vorher stand hier der allgemeine EmptyState (graues Icon, zwei Zeilen, Knopf) —
 * derselbe Baustein, den auch eine leere Benachrichtigungsliste benutzt. Der
 * Einstieg in die Runde ist aber kein Leerzustand, sondern der Anfang: Er sieht
 * jetzt aus wie das, was danach kommt, und sagt in drei Zeilen, wie die Runde
 * funktioniert.
 */
const RoundStartCard = memo(({ isDrawing, onDraw, onOpenSearch }) => {
  // Direkt aus localStorage statt useAuth(): dieselbe Quelle, die auch
  // AuthContext beim App-Start fuer den Logo-Preload nutzt, aber ohne die
  // Karte an einen AuthProvider zu binden, der hier gar nicht gebraucht wird.
  const schoolId = localStorage.getItem('schoolId');
  // Synchron aus dem Cache (kein Netzwerk-Call hier) — Schullogo, falls
  // hochgeladen, sonst das Otter-Logo als neutraler Absender der Karte.
  const schoolLogo = getCachedSchoolLogo(schoolId);
  // Der Cache kann eine URL enthalten, die inzwischen ins Leere zeigt (Logo
  // geloescht, Pfad geaendert) — dann zeigte der Browser das kaputte-Bild-Icon
  // statt eines Fallbacks. onError schaltet fuer DIESEN Render permanent auf
  // das Otter-Logo um.
  const [logoFailed, setLogoFailed] = useState(false);
  const showSchoolLogo = schoolLogo && !logoFailed;

  return (
  <div className="relative w-full max-w-sm mx-auto overflow-hidden rounded-3xl border border-border bg-surface p-6 text-center shadow-token-md">
    {/* Derselbe theme-neutrale Aura-Lichtschein wie im Kopf der Profilkarte. */}
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-1/2"
      style={{ background: 'radial-gradient(ellipse 90% 100% at 50% 0%, rgb(var(--color-primary) / 0.12), transparent 72%)' }}
    />

    <div className="relative">
      {showSchoolLogo ? (
        /* Schullogos sind fast immer breite Wortmarken (Wappen + Schulname).
           Im runden Rahmen mit object-cover wurde das Bild auf ein Quadrat
           gezoomt und dann rund beschnitten — der Schulname fiel links und
           rechts weg. Das Logo steht deshalb frei: feste Hoehe, Breite nach
           Bedarf, object-contain — es wird nichts mehr abgeschnitten. */
        <img
          src={schoolLogo}
          alt="Schullogo"
          className="mx-auto h-16 w-auto max-w-[70%] object-contain"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        /* Das Otter-Logo ist quadratisch und als Marken-Zeichen fuer den
           runden Rahmen gemacht — hier bleibt der Kreis. */
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-border bg-surface-raised shadow-token-sm overflow-hidden">
          <OtterCompactLogo className="h-9 w-9" />
        </div>
      )}

      <h2 className="mt-4 text-xl font-bold tracking-tight text-foreground">
        Deine Leute für heute
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Hier kommen sechs Leute aus deiner Schule, die zu deinen Skills
        passen — jemand, der dir weiterhilft, oder jemand, dem du weiterhelfen kannst.
      </p>

      <ul className="mt-5 space-y-2.5 text-left stagger-children">
        {START_HINTS.map(({ Icon, text }) => (
          <li key={text} className="flex items-center gap-2.5 text-sm text-foreground">
            <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            {text}
          </li>
        ))}
        {/* Die dritte Zeile erklärt die Bedienung — und die ist auf dem Telefon
            (wischen) eine andere als am großen Bildschirm (Raster). */}
        <li className="flex items-center gap-2.5 text-sm text-foreground md:hidden">
          <ChevronUp className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          Wisch nach oben für die nächste
        </li>
        <li className="hidden items-center gap-2.5 text-sm text-foreground md:flex">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          Alle sechs auf einen Blick
        </li>
      </ul>

      <Button onClick={onDraw} disabled={isDrawing} fullWidth className="mt-6">
        {isDrawing ? 'Wird gemischt…' : 'Runde starten'}
      </Button>

      <button
        type="button"
        onClick={onOpenSearch}
        className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
      >
        <Search className="h-3.5 w-3.5" aria-hidden="true" />
        Jemand Bestimmtes suchen
      </button>
    </div>
  </div>
  );
});
RoundStartCard.displayName = 'RoundStartCard';
RoundStartCard.propTypes = {
  isDrawing: PropTypes.bool,
  onDraw: PropTypes.func.isRequired,
  onOpenSearch: PropTypes.func.isRequired
};

/**
 * Die Schlusskarte. Sie ist der eigentliche Unterschied zur Endlosliste: Die
 * Ansicht hört auf und sagt, wann es weitergeht — statt immer weiterzuscrollen.
 */
const RoundEndCard = memo(({ round, poolExhausted, isEmpty, isDrawing, onDraw, onOpenSearch, onBackToTop }) => {
  const canDraw = round?.canDrawNow ?? false;
  // Eine Runde ohne Karten: Alle Gezogenen sind inzwischen vernetzt, blockiert
  // oder ausgeblendet (sie fallen aus der Runde, nichts rückt nach), oder es war
  // niemand frei. "Das waren deine sechs" stimmte dann nicht, und "Zurück zur
  // ersten Karte" führte ins Leere (Nov 2026).
  let title = poolExhausted ? 'Mehr gibt es gerade nicht' : 'Das waren deine sechs';
  let text = poolExhausted
    ? 'Du hast alle gesehen, die gerade zu dir passen. Morgen sind wieder neue dabei.'
    : 'Schau sie dir in Ruhe an — oder misch dir neue Vorschläge.';
  if (isEmpty) {
    title = 'Gerade niemand Neues';
    text = 'Alle, die gerade zu dir passen, hast du schon gesehen oder bist schon mit ihnen vernetzt. Morgen sind wieder neue dabei.';
  }
  const roundsLeft = round?.roundsLeft ?? 0;
  const countdown = formatCountdown(round?.nextRoundAvailableInSeconds);

  return (
    /* Die Folie (.match-slide) sitzt am Elternelement — hier bleibt nur das
       Zentrieren darin, damit die Schlusskarte genauso eine Bildschirmseite
       ist wie die Profilkarten und das Einrasten gleichmäßig bleibt. */
    <div className="flex h-full items-center justify-center">
      {/* max-w-lg wie die Profilkarten im selben Stapel; im Raster ab md faellt
          die Grenze weg, damit die Schlusskarte ihre Spalte fuellt statt als
          schmaler Streifen darin zu stehen. */}
      <div className="w-full max-w-lg md:max-w-none mx-auto rounded-3xl border border-border bg-surface p-6 text-center shadow-token-md">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>

        <p className="mt-2 text-sm text-muted-foreground">{text}</p>

        {canDraw ? (
          <>
            <Button onClick={onDraw} disabled={isDrawing} fullWidth className="mt-5">
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              <span>{isDrawing ? 'Wird gemischt…' : 'Neu mischen'}</span>
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              {roundsLeft === 1 ? 'Noch eine Runde heute' : `Noch ${roundsLeft} Runden heute`}
            </p>
          </>
        ) : (
          <div className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-border bg-surface-raised p-3 text-sm text-muted-foreground">
            <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {countdown ? `Deine nächste Runde: in ${countdown}` : 'Morgen gibt es neue Vorschläge'}
            </span>
          </div>
        )}

        {/* Der Weg zurück in die Runde. Ohne ihn ist die Schlusskarte eine
            Sackgasse — besonders wenn das Tageskontingent aufgebraucht ist und
            hier sonst nur ein Countdown steht. */}
        {!isEmpty && (
          <Button variant="secondary" onClick={onBackToTop} fullWidth className="mt-3">
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
            <span>Zurück zur ersten Karte</span>
          </Button>
        )}

        {/* Der ruhige zweite Weg: Wer jemand Bestimmtes sucht, ist im
            Suchmodus richtig — dort gilt weder Kontingent noch Cooldown. */}
        <button
          type="button"
          onClick={onOpenSearch}
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
        >
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
          Jemand Bestimmtes suchen
        </button>
      </div>
    </div>
  );
});
RoundEndCard.displayName = 'RoundEndCard';
RoundEndCard.propTypes = {
  round: PropTypes.object,
  poolExhausted: PropTypes.bool,
  isEmpty: PropTypes.bool,
  isDrawing: PropTypes.bool,
  onDraw: PropTypes.func.isRequired,
  onOpenSearch: PropTypes.func.isRequired,
  onBackToTop: PropTypes.func.isRequired
};

const MatchRoundView = memo(({
  profiles,
  round,
  meta,
  isLoading,
  isDrawing,
  error,
  userProfile,
  bannerHeight,
  onDraw,
  onSnooze,
  onBlocked,
  onOpenSearch,
  onRetry,
  active = true
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [freshRoundLabel, setFreshRoundLabel] = useState(null);
  const cardRefs = useRef([]);
  const feedRef = useRef(null);
  // Zusätzlich als State, damit Effekte anlaufen, sobald der Knoten steht —
  // eine Ref-Zuweisung allein löst kein Rendern aus.
  const [feedNode, setFeedNode] = useState(null);

  const attachFeed = useCallback((node) => {
    feedRef.current = node;
    setFeedNode(node);
  }, []);

  // Wieviel Platz bleibt dem Kartenbereich? Das wird gemessen, nicht gerechnet.
  //
  // Kopfleiste, Seitenkopf, Session-Banner und Fehlermeldung verschieben den Feed
  // unterschiedlich weit nach unten; als Konstante gepflegt (vorher
  // --match-card-reserve: 82px) war der Wert zweimal falsch.
  //
  // Bis August 2026 wurde nur der Abstand NACH OBEN gemessen und in der CSS von
  // 100dvh abgezogen. Das ging in der Capacitor-WebView daneben: dvh und die
  // tatsaechlich sichtbare Hoehe fallen dort um ein paar Pixel auseinander, der
  // Feed wurde zu hoch — und genau diese Pixel fuellte die naechste Folie, als
  // gerader weisser Streifen unter dem Vernetzen-Knopf. Jetzt wird der Rest
  // direkt gemessen: sichtbare Hoehe minus Oberkante des Feeds. Damit ist keine
  // Annahme ueber dvh mehr im Spiel.
  //
  // getBoundingClientRect().top ist bereits fensterrelativ — window.scrollY darf
  // hier NICHT dazu, sonst schrumpft der Feed, sobald die Seite gescrollt ist.
  useLayoutEffect(() => {
    const node = feedNode;
    // Verdeckter Reiter (MainScreen, Reiter "Skills"): Die Ansicht bleibt
    // eingehängt, damit Karte und Scrollstand erhalten bleiben, liegt aber auf
    // display:none. Gemessen würde dort nur Unsinn (alles 0). Beim Zurückwechseln
    // läuft der Effekt über `active` neu an.
    if (!node || !active) return undefined;

    const setVar = (name, value) => {
      if (node.style.getPropertyValue(name) !== value) {
        node.style.setProperty(name, value);
      }
    };

    const measure = () => {
      const top = node.getBoundingClientRect().top;
      // visualViewport kennt die wirklich sichtbare Flaeche (eingeblendete
      // Tastatur, Browserleisten); innerHeight ist der Rueckfall.
      const viewportHeight = (typeof window.visualViewport !== 'undefined' && window.visualViewport)
        ? window.visualViewport.height
        : window.innerHeight;

      // Nur schreiben, wenn sich etwas ändert: sonst könnte der ResizeObserver
      // unten sich selbst immer wieder auslösen.
      setVar('--match-feed-space', `${Math.max(0, Math.round(viewportHeight - top))}px`);
      // Rueckfallebene fuer die CSS-Zeile davor (und fuer Umgebungen ohne Layout,
      // z.B. jsdom in den Tests), weiterhin dokumentrelativ.
      setVar('--match-feed-top', `${Math.max(0, Math.round(top + (window.scrollY || 0)))}px`);

      // Wo genau faengt die Navigationsleiste an? Auch das wird gemessen statt
      // geschaetzt.
      //
      // Ab Tablet (600px) steht die Leiste als schmale Spalte LINKS, nicht mehr
      // unten. `navTop > top` ist dort falsch, der Zweig faellt also absichtlich
      // durch und --match-feed-height bleibt ungesetzt. Das ist kein Versehen:
      // Zwischen 600 und 767px greift dann Stufe 3 (--match-feed-space minus
      // dem auf den Fussabstand zurueckgefallenen --bottom-nav-clearance), ab
      // 768px gilt `.match-feed { height: auto }` (index.css). --bottom-nav-clearance (96px + Safe Area) ist app-weit die
      // richtige Reserve, fuer die Karte aber rund 14px zu grosszuegig: Die
      // schwebende Leiste braucht real 21px Abstand + Kapsel. Zusammen mit dem
      // Fussabstand der Folie klaffte darunter eine Luecke, die nach Fehler aussah.
      // An der Leiste gemessen kann der Wert nie wieder von ihrer echten Hoehe
      // abdriften — derselbe Grund, aus dem --match-card-reserve hier abgeloest wurde.
      const nav = document.querySelector('[data-bottom-nav]');
      const navTop = nav ? nav.getBoundingClientRect().top : null;
      // Pflichtpruefung: Bei offener Tastatur steht die Leiste auf display:none
      // (body.keyboard-open .keyboard-hide-on-open) und liefert lauter Nullen —
      // ungeprueft uebernommen faellt die Karte auf ihre Mindesthoehe zusammen.
      const navUsable = nav
        && nav.getBoundingClientRect().height > 0
        && navTop > top;

      if (navUsable) setVar('--match-feed-height', `${Math.round(navTop - top)}px`);
      else node.style.removeProperty('--match-feed-height');
    };

    measure();
    // Ein zweiter Durchgang nach dem ersten Frame: Schriften und Bilder oberhalb
    // sind dann gesetzt.
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(measure) : null;

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    const viewport = typeof window.visualViewport !== 'undefined' ? window.visualViewport : null;
    viewport?.addEventListener('resize', measure);

    // Sicherheitsnetz für alles, was oberhalb später auftaucht, ohne dass diese
    // Komponente neu rendert (z.B. der Hinweis auf offene Logbucheinträge).
    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && node.parentElement) {
      observer = new ResizeObserver(measure);
      observer.observe(node.parentElement);
    }

    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      viewport?.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [feedNode, bannerHeight, error, isLoading, profiles.length, active]);

  // Welche Karte ist gerade zu sehen? Über einen Observer statt über
  // Scroll-Ereignisse, damit während des Scrollens nicht bei jedem Frame
  // neu gerendert wird. `root` ist der Feed — er scrollt, nicht das Fenster.
  useEffect(() => {
    // Nach einem "Später zeigen" ist die Runde kürzer: alles jenseits der
    // aktuellen Karten (+ Schlusskarte) wegschneiden, damit keine abgehängten
    // Knoten beobachtet werden.
    cardRefs.current.length = profiles.length + 1;
    const nodes = cardRefs.current.filter(Boolean);
    if (!feedNode || nodes.length === 0 || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.cardIndex);
            if (!Number.isNaN(index)) setActiveIndex(index);
          }
        });
      },
      { root: feedNode, threshold: 0.6 }
    );

    nodes.forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, [feedNode, profiles.length]);

  // Der Tick beim Einrasten (Aug 2026).
  //
  // Der Feed rastet ein — sichtbar, aber bis hierhin nicht fuehlbar. Ein
  // kurzer Selection-Impuls pro Karte ist genau das, was iOS-Picker und
  // Story-Ansichten machen: Man merkt am Finger, dass eine Karte weiter ist,
  // ohne hinzusehen. `haptic.selection()` ist der leichteste Impuls, den es
  // gibt (auf Android ein Tick) und im Browser stiller No-Op.
  //
  // Der Ref haelt den zuletzt bestaetigten Stand. Ohne ihn feuerte der Impuls
  // schon beim ersten Aufbau der Ansicht, wenn der Beobachter Karte 0 meldet —
  // ein Buzzen, ohne dass jemand etwas getan hat.
  const hapticIndexRef = useRef(null);
  useEffect(() => {
    const previous = hapticIndexRef.current;
    hapticIndexRef.current = activeIndex;
    if (previous === null || previous === activeIndex) return;
    haptic.selection();
  }, [activeIndex]);

  // Neue Runde ziehen ist der eine Knopf, hinter dem eine Wartezeit steckt.
  // Der leichte Impuls quittiert den Druck sofort (Aktion starten = light,
  // seit Oktober 2026) — die Karten kommen erst, wenn der Server geantwortet
  // hat, und ihre Ankunft quittiert useMatchRound mit success.
  const handleDraw = useCallback(() => {
    haptic.light();
    onDraw();
  }, [onDraw]);

  const scrollFeedToTop = useCallback(() => {
    const node = feedRef.current;
    if (node) {
      if (typeof node.scrollTo === 'function') node.scrollTo({ top: 0 });
      else node.scrollTop = 0;
    }
    // Am großen Bildschirm scrollt nicht der Feed, sondern die Seite.
    if (typeof window !== 'undefined' && window.scrollY > 0) window.scrollTo({ top: 0 });
    setActiveIndex(0);
  }, []);

  // Nach dem Mischen zurück an den Anfang — sonst bleibt man auf der
  // Schlusskarte stehen, wo der Knopf sitzt, und merkt vom Mischen nichts.
  const roundNumber = round?.number ?? null;
  const maxPerDay = round?.maxPerDay ?? 0;
  const previousRoundRef = useRef(null);

  useEffect(() => {
    if (roundNumber === null) return undefined;
    const previous = previousRoundRef.current;
    previousRoundRef.current = roundNumber;

    // Erstes Laden ist kein Mischen.
    if (previous === null || previous === roundNumber) return undefined;

    scrollFeedToTop();

    // Beim allerersten Ziehen (vorher gab es keine Runde) führt schon der
    // Wechsel von der Einstiegskarte zu den Karten vor Augen, dass etwas
    // passiert ist — die Pille wäre dort nur Lärm.
    if (previous < 1) return undefined;

    setFreshRoundLabel(`Runde ${roundNumber} von ${maxPerDay} · frisch gemischt`);
    const timer = setTimeout(() => setFreshRoundLabel(null), 2200);
    return () => clearTimeout(timer);
  }, [roundNumber, maxPerDay, scrollFeedToTop]);

  const showBlockingSpinner = isLoading && profiles.length === 0;
  const hasRoundStarted = (round?.roundsUsed ?? 0) > 0;

  // Nach einem Fehler vor der ersten Runde stand bis September 2026 nur der rote
  // Kasten da — ohne Knopf, und neu geladen wurde erst beim nächsten Mount von
  // MainScreen. Welcher Ausweg passt, hängt davon ab, ob der Stand bekannt ist:
  // - `round` fehlt: Schon das Laden ist gescheitert. Dann nur "Erneut versuchen"
  //   (lädt, verbraucht nichts). Die Einstiegskarte wäre hier falsch — wer heute
  //   schon gezogen hat, würde mit "Runde starten" eine weitere Runde verbrauchen.
  // - `round` ist da, aber noch nicht gezogen: Das Ziehen ist gescheitert. Die
  //   Einstiegskarte bleibt stehen, ihr Knopf ist der Neuversuch.
  const roundStateUnknown = !round;
  const showStartCard = !hasRoundStarted && profiles.length === 0 && (!error || !roundStateUnknown);
  const showRetry = Boolean(error && roundStateUnknown && onRetry);

  // Kein eigener Kopf mehr (Okt 2026, Skills entdecken Phase 3): Die Zeile
  // "Entdecken · 1 von 3" mit Lupe ist der Kopfzeile von MainScreen gewichen
  // (discover/DiscoverHeader: Pillen "Personen | Skills" + Lupe). Den Stand der
  // Runde zeigen Start- und Schlusskarte; ausgeschrieben steht er weiter in der
  // Pille nach dem Mischen. Der Kopf steht damit VOR dem gemessenen Feed, die
  // Messung oben nimmt ihn mit.
  return (
    <>
      {showBlockingSpinner && (
        <div className="max-w-sm mx-auto" aria-busy="true">
          <span className="sr-only">Deine Runde wird gemischt…</span>
          <Skeleton.Card />
        </div>
      )}

      {!showBlockingSpinner && (
        <>
          {error && (
            <div className="mb-4 rounded-xl border border-danger/40 bg-danger-soft p-3 sm:p-4 text-foreground" role="alert">
              <p>{error}</p>
              {showRetry && (
                <Button variant="secondary" size="sm" onClick={onRetry} className="mt-3">
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  <span>Erneut versuchen</span>
                </Button>
              )}
            </div>
          )}

          {/* Noch keine Runde gezogen: ein einzelner, klarer Einstieg — im
              selben Kartenbereich, damit die Höhenrechnung eine bleibt. */}
          {showStartCard && (
            <div ref={attachFeed} className="match-feed flex items-center justify-center">
              <RoundStartCard
                isDrawing={isDrawing}
                onDraw={handleDraw}
                onOpenSearch={onOpenSearch}
              />
            </div>
          )}

          {hasRoundStarted && (
            /* Bühne: trägt Positionsleiste und Bestätigung, ohne selbst zu scrollen. */
            <div className="relative">
              <RoundProgress total={profiles.length + 1} activeIndex={activeIndex} />
              <FreshRoundPill label={freshRoundLabel} />

              {/* Telefon: ein Scroll-Container, eine Karte pro Bildschirmseite,
                  Einrasten beim Wischen. Ab md: ein ruhiges Raster mit gleich
                  hohen Karten, in dem die ganze Runde auf einen Blick liegt.
                  Den Unterschied macht allein die Medienabfrage in index.css. */}
              <div
                ref={attachFeed}
                // Drei Spalten erst ab xl: Seit die Navigation ab md als Spalte
                // links steht, kostet sie 88px. Auf 1024px blieben mit drei
                // Spalten rund 280px je Karte — enger als jedes Telefon.
                className="match-feed md:grid md:grid-cols-2 xl:grid-cols-3 md:gap-4"
              >
                {profiles.map((profile, index) => (
                  <div
                    key={profile.id}
                    ref={node => { cardRefs.current[index] = node; }}
                    data-card-index={index}
                    className="match-slide"
                  >
                    <ProfileCard
                      profile={profile}
                      currentUser={userProfile}
                      onSnooze={onSnooze}
                      onBlocked={onBlocked}
                      origin="ROUND"
                      fill
                    />
                  </div>
                ))}

                <div
                  ref={node => { cardRefs.current[profiles.length] = node; }}
                  data-card-index={profiles.length}
                  className="match-slide"
                >
                  <RoundEndCard
                    round={round}
                    poolExhausted={!!meta?.poolExhausted}
                    isEmpty={profiles.length === 0}
                    isDrawing={isDrawing}
                    onDraw={handleDraw}
                    onOpenSearch={onOpenSearch}
                    onBackToTop={scrollFeedToTop}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
});

MatchRoundView.displayName = 'MatchRoundView';

MatchRoundView.propTypes = {
  profiles: PropTypes.array.isRequired,
  round: PropTypes.object,
  meta: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  isDrawing: PropTypes.bool,
  error: PropTypes.string,
  userProfile: PropTypes.object.isRequired,
  // Höhe eines eingeblendeten Session-Banners: verschiebt den Kartenbereich
  // nach unten und muss deshalb eine Neumessung auslösen.
  bannerHeight: PropTypes.number,
  onDraw: PropTypes.func.isRequired,
  onSnooze: PropTypes.func.isRequired,
  onBlocked: PropTypes.func,
  onOpenSearch: PropTypes.func.isRequired,
  // Lädt die Runde neu, wenn schon das erste Laden gescheitert ist.
  onRetry: PropTypes.func,
  // Reiter "Personen" sichtbar? Löst beim Zurückwechseln eine Neumessung aus.
  active: PropTypes.bool
};

export default MatchRoundView;
